import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, StatTile, PageHeader, Avatar, RoleBadge, EligibilityBadge, Loading, fmtDateTime, btnPrimary } from "@/components/kit";
import { House, Users, Barbell, CalendarBlank, Megaphone, GraduationCap, Heartbeat, Drop, Moon, Fire, Trophy, ArrowRight, WarningCircle } from "@phosphor-icons/react";

function EventRow({ e }) {
  const isGame = e.type === "game";
  return (
    <div className="flex items-center gap-3 py-3 border-b border-white/5 last:border-0" data-testid={`event-${e.id}`}>
      <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${isGame ? "bg-[#FF3B30]/15" : "bg-white/5"}`}>
        {isGame ? <Trophy size={18} weight="fill" color="#FF3B30" /> : <Barbell size={18} weight="bold" color="#A3A3A3" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-white text-sm truncate">{e.title}{e.opponent ? ` vs ${e.opponent}` : ""}</div>
        <div className="text-xs text-[#737373]">{e.team_name} · {e.location}</div>
      </div>
      <div className="text-xs font-mono text-[#A3A3A3] text-right shrink-0">{fmtDateTime(e.start)}</div>
    </div>
  );
}

function AnnRow({ a }) {
  return (
    <div className="py-3 border-b border-white/5 last:border-0" data-testid={`announcement-${a.id}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="font-semibold text-white text-sm">{a.title}</div>
        <span className="text-[10px] uppercase tracking-widest text-[#FF3B30] font-bold shrink-0">{a.audience_name}</span>
      </div>
      <p className="text-sm text-[#A3A3A3] leading-relaxed">{a.body}</p>
      <div className="text-[11px] text-[#737373] mt-1">— {a.author_name}</div>
    </div>
  );
}

function Panel({ title, icon: Icon, children, action }) {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold">
          <Icon size={16} weight="fill" color="#FF3B30" /> {title}
        </div>
        {action}
      </div>
      {children}
    </Card>
  );
}

const empty = (txt) => <div className="text-sm text-[#737373] py-6 text-center">{txt}</div>;

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <Loading label="Loading your hub…" />;

  const greeting = `Hi ${user?.name?.split(" ")[0]}`;

  return (
    <div className="px-4 md:px-8 py-8 md:py-12 max-w-7xl mx-auto">
      <PageHeader eyebrow={<RoleBadge role={user?.role} />} title={greeting} accent="welcome." icon={House} />

      {/* ATHLETIC DIRECTOR */}
      {data.role === "athletic_director" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
            <StatTile label="Athletes" value={data.counts.athlete} testid="stat-athletes" />
            <StatTile label="Teams" value={data.teams} color="#FF9F0A" testid="stat-teams" />
            <StatTile label="Attendance" value={`${data.attendance_rate}%`} color="#34C759" testid="stat-attendance" />
            <StatTile label="Staff" value={data.counts.coach + data.counts.teacher} color="#0A84FF" testid="stat-staff" />
          </div>
          {data.ineligible_count > 0 && (
            <button onClick={() => navigate("/app/people")} className="w-full flex items-center gap-3 mb-6 px-5 py-4 rounded-sm bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-left hover:bg-[#FF3B30]/15 transition-colors" data-testid="ad-ineligible-banner">
              <WarningCircle size={22} weight="fill" color="#FF3B30" />
              <span className="text-sm text-white font-semibold">{data.ineligible_count} athlete{data.ineligible_count > 1 ? "s are" : " is"} currently ineligible</span>
              <span className="ml-auto text-xs text-[#FF3B30] font-semibold uppercase tracking-widest">Review →</span>
            </button>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <Panel title="Upcoming Events" icon={CalendarBlank} action={<button onClick={() => navigate("/app/schedule")} className="text-xs text-[#FF3B30] font-semibold uppercase">View all</button>}>
              {data.upcoming_events.length ? data.upcoming_events.slice(0, 5).map((e) => <EventRow key={e.id} e={e} />) : empty("No upcoming events")}
            </Panel>
            <Panel title="Announcements" icon={Megaphone}>
              {data.announcements.length ? data.announcements.map((a) => <AnnRow key={a.id} a={a} />) : empty("No announcements")}
            </Panel>
            <Panel title="Recently Added People" icon={Users} action={<button onClick={() => navigate("/app/people")} className="text-xs text-[#FF3B30] font-semibold uppercase">Manage</button>}>
              <div className="space-y-2">
                {data.recent_users.map((u) => (
                  <div key={u.id} className="flex items-center gap-3 py-1">
                    <Avatar name={u.name} size={34} />
                    <div className="flex-1"><div className="text-sm text-white font-medium">{u.name}</div></div>
                    <RoleBadge role={u.role} />
                  </div>
                ))}
              </div>
            </Panel>
          </div>
        </>
      )}

      {/* COACH */}
      {data.role === "coach" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
            <StatTile label="My Teams" value={data.teams.length} testid="stat-teams" />
            <StatTile label="My Athletes" value={data.athlete_count} color="#34C759" testid="stat-athletes" />
            <StatTile label="Ineligible" value={data.ineligible_count} color="#FF3B30" testid="stat-ineligible" />
            <StatTile label="Upcoming" value={data.upcoming_events.length} unit="events" color="#FF9F0A" testid="stat-events" />
          </div>
          {data.ineligible_athletes?.length > 0 && (
            <Card className="p-5 mb-6 border-[#FF3B30]/30 bg-[#FF3B30]/5" data-testid="coach-ineligible-panel">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FF3B30] font-semibold mb-3">
                <WarningCircle size={16} weight="fill" /> Ineligible Athletes
              </div>
              <div className="space-y-2">
                {data.ineligible_athletes.map((a) => (
                  <div key={a.id} className="flex items-center gap-3" data-testid={`ineligible-${a.id}`}>
                    <Avatar name={a.name} size={32} />
                    <div className="flex-1 min-w-0"><div className="text-sm text-white font-medium truncate">{a.name}</div>{a.eligibility_reason && <div className="text-xs text-[#737373] truncate">{a.eligibility_reason}</div>}</div>
                    <EligibilityBadge eligible={false} testid={`elig-badge-${a.id}`} />
                  </div>
                ))}
              </div>
            </Card>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <Panel title="My Teams" icon={Barbell} action={<button onClick={() => navigate("/app/teams")} className="text-xs text-[#FF3B30] font-semibold uppercase">Open</button>}>
              {data.teams.map((t) => (
                <div key={t.id} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                  <div><div className="font-semibold text-white">{t.name}</div><div className="text-xs text-[#737373]">{t.sport}</div></div>
                  <span className="font-mono text-[#A3A3A3]">{t.athlete_count} athletes</span>
                </div>
              ))}
            </Panel>
            <Panel title="Upcoming Events" icon={CalendarBlank} action={<button onClick={() => navigate("/app/schedule")} className={"text-xs text-[#FF3B30] font-semibold uppercase"}>Schedule</button>}>
              {data.upcoming_events.length ? data.upcoming_events.slice(0, 5).map((e) => <EventRow key={e.id} e={e} />) : empty("No events scheduled")}
            </Panel>
            <Panel title="Announcements" icon={Megaphone}>
              {data.announcements.length ? data.announcements.map((a) => <AnnRow key={a.id} a={a} />) : empty("No announcements")}
            </Panel>
          </div>
        </>
      )}

      {/* TEACHER */}
      {data.role === "teacher" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-6">
            <StatTile label="My Students" value={data.student_count} testid="stat-students" />
            <StatTile label="Grades Logged" value={data.recent_grades.length} color="#FF9F0A" testid="stat-grades" />
            <StatTile label="Announcements" value={data.announcements.length} color="#0A84FF" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <Panel title="My Students · Today's Wellness" icon={Heartbeat} action={<button onClick={() => navigate("/app/academics")} className="text-xs text-[#FF3B30] font-semibold uppercase">Academics</button>}>
              <div className="space-y-3" data-testid="teacher-wellness-list">
                {(data.students_wellness || []).map(({ user: s, wellness: w }) => (
                  <div key={s.id} className="flex items-center gap-3 py-1.5 border-b border-white/5 last:border-0" data-testid={`student-wellness-${s.id}`}>
                    <Avatar name={s.name} size={34} color="#34C759" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-white font-medium truncate">{s.name}</span>
                        {s.role === "athlete" && <EligibilityBadge eligible={s.eligible} testid={`student-elig-${s.id}`} />}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-[#737373] mt-0.5">
                        <span className="flex items-center gap-1"><Moon size={13} weight="fill" color="#BF5AF2" />{w.sleep_hours || 0}h</span>
                        <span className="flex items-center gap-1"><Drop size={13} weight="fill" color="#0A84FF" />{w.water_glasses || 0}</span>
                        <span className="flex items-center gap-1"><Fire size={13} weight="fill" color="#FF9F0A" />{Math.round(w.calories || 0)} kcal</span>
                        {w.sleep_hours > 0 && w.sleep_hours < 6 && <span className="text-[#FF3B30] font-semibold uppercase tracking-wider">Low sleep</span>}
                      </div>
                    </div>
                  </div>
                ))}
                {(!data.students_wellness || data.students_wellness.length === 0) && empty("No students assigned")}
              </div>
            </Panel>
            <Panel title="Announcements" icon={Megaphone}>
              {data.announcements.length ? data.announcements.map((a) => <AnnRow key={a.id} a={a} />) : empty("No announcements")}
            </Panel>
          </div>
        </>
      )}

      {/* ATHLETE */}
      {data.role === "athlete" && (
        <>
          <div className="mb-6 flex items-center gap-3">
            <EligibilityBadge eligible={data.eligible} testid="athlete-eligibility-badge" />
            <span className="text-xs text-[#737373]">{data.eligible === false ? "You are currently ineligible to compete." : "You are cleared to compete."}</span>
          </div>
          {data.eligible === false && data.eligibility_reason && (
            <Card className="p-4 mb-6 border-[#FF3B30]/30 bg-[#FF3B30]/5 flex items-center gap-3" data-testid="athlete-ineligible-banner">
              <WarningCircle size={20} weight="fill" color="#FF3B30" />
              <span className="text-sm text-white">{data.eligibility_reason}</span>
            </Card>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
            <StatTile label="GPA Score" value={data.gpa} testid="stat-gpa" />
            <StatTile label="Attendance" value={`${data.attendance_rate}%`} color="#34C759" testid="stat-attendance" />
            <StatTile label="Sleep" value={data.wellness.sleep_hours} unit="h" color="#BF5AF2" testid="stat-sleep" />
            <StatTile label="Water" value={data.wellness.water_glasses} unit="cups" color="#0A84FF" testid="stat-water" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <Panel title="Today's Fuel" icon={Fire} action={<button onClick={() => navigate("/app/wellness")} className="text-xs text-[#FF3B30] font-semibold uppercase">Log</button>}>
              <div className="flex items-end gap-3 mb-3">
                <span className="font-mono text-4xl font-black" data-testid="dash-calories">{Math.round(data.wellness.calories)}</span>
                <span className="text-[#737373] mb-1 font-mono">/ {data.macro_goals.calories} kcal</span>
              </div>
              <div className="h-2.5 w-full bg-white/5 rounded-sm overflow-hidden">
                <div className="h-full bg-[#FF3B30]" style={{ width: `${Math.min(100, (data.wellness.calories / data.macro_goals.calories) * 100)}%` }} />
              </div>
              <button onClick={() => navigate("/app/wellness")} className={`${btnPrimary} mt-5 w-full`} data-testid="dash-scan-button">Track wellness & meals <ArrowRight size={16} weight="bold" /></button>
            </Panel>
            <Panel title="Upcoming Events" icon={CalendarBlank} action={<button onClick={() => navigate("/app/schedule")} className="text-xs text-[#FF3B30] font-semibold uppercase">All</button>}>
              {data.upcoming_events.length ? data.upcoming_events.slice(0, 5).map((e) => <EventRow key={e.id} e={e} />) : empty("No upcoming events")}
            </Panel>
            <Panel title="Announcements" icon={Megaphone}>
              {data.announcements.length ? data.announcements.map((a) => <AnnRow key={a.id} a={a} />) : empty("No announcements")}
            </Panel>
          </div>
        </>
      )}

      {/* PARENT */}
      {data.role === "parent" && (
        <>
          {data.children.map((c) => (
            <Card key={c.user.id} className="p-6 mb-6" data-testid={`child-card-${c.user.id}`}>
              <div className="flex items-center gap-3 mb-5">
                <Avatar name={c.user.name} size={48} color="#34C759" />
                <div>
                  <div className="font-head text-2xl font-bold uppercase tracking-tight">{c.user.name}</div>
                  <div className="text-xs text-[#737373]">{c.user.sport}</div>
                </div>
                {c.user.role === "athlete" && <div className="ml-auto"><EligibilityBadge eligible={c.user.eligible} testid={`child-elig-${c.user.id}`} /></div>}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatTile label="GPA" value={c.gpa} testid={`child-gpa-${c.user.id}`} />
                <StatTile label="Attendance" value={`${c.attendance_rate}%`} color="#34C759" />
                <StatTile label="Sleep" value={c.wellness.sleep_hours} unit="h" color="#BF5AF2" />
                <StatTile label="Calories" value={Math.round(c.wellness.calories)} color="#FF9F0A" />
              </div>
            </Card>
          ))}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <Panel title="Upcoming Events" icon={CalendarBlank} action={<button onClick={() => navigate("/app/schedule")} className="text-xs text-[#FF3B30] font-semibold uppercase">All</button>}>
              {data.upcoming_events.length ? data.upcoming_events.slice(0, 5).map((e) => <EventRow key={e.id} e={e} />) : empty("No upcoming events")}
            </Panel>
            <Panel title="Announcements" icon={Megaphone}>
              {data.announcements.length ? data.announcements.map((a) => <AnnRow key={a.id} a={a} />) : empty("No announcements")}
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}
